import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { useMount } from 'react-use';
import { useUppy } from '@uppy/react';
import AwsS3Multipart from '@uppy/aws-s3-multipart';
import { Uppy, type PluginOptions } from '@uppy/core';
import type { UploadResult } from '@uppy/core';
import {
  initialState,
  uploadActions,
  uploadReducer,
  type UploadFilesState,
} from './reducer';
import {
  rtkApi,
  UploadedFile,
  useCommitProjectAssetMutation,
  useCreateActivityMutation,
  useCreateProjectAssetMutation,
  useGetUploadedFilesQuery,
} from '../../../redux/rtk';
import { ACTIVITY_TYPES } from '../../../constants/activity';
import { AssetUploadOrigin, assetUploadOriginEnum } from '@larki/shared/enums';
import { useDispatch } from 'react-redux';
import { getUser } from '../../../utilities/storage';

interface AwsS3MultipartOptions extends PluginOptions {
  limit?: number;
  companionUrl?: string;
  companionHeaders?: Record<string, string>;
  createMultipartUpload?: (this: any, file: any) => Promise<any>;
}

type UploadApiContext = {
  state: UploadFilesState;
  dispatch: React.Dispatch<
    | {
        type: 'upload/addFile';
        payload: { id: string; name: string; type?: string };
      }
    | { type: 'upload/removeFile'; payload: string }
    | {
        type: 'upload/updateProgress';
        payload: { id: string; progress: number };
      }
    | { type: 'upload/setUploading'; payload: boolean }
    | { type: 'upload/setError'; payload: string | null }
    | {
        type: 'upload/setFileError';
        payload: { id: string; error: string | null };
      }
    | { type: 'upload/clearAllFileErrors' }
    | { type: 'upload/reset' }
  >;
  uppy: Uppy;
  uploadedFiles: UploadedFile[];
};

const UploadContext = createContext<UploadApiContext | null>(null);

export const useUploadContext = (): UploadApiContext => {
  const ctx = useContext(UploadContext);
  if (!ctx)
    throw new Error('useUploadContext must be used within UploadProvider');
  return ctx;
};

export const UploadProvider: React.FC<{
  children: React.ReactNode;
  jobId?: number;
  projectId: number | null;
  autoUpload?: boolean;
  onUploadComplete?: (result: UploadResult) => void;
}> = ({ children, jobId, projectId, autoUpload = false, onUploadComplete }) => {
  const reduxDispatch = useDispatch();
  const [state, dispatch] = useReducer(uploadReducer, initialState);
  const [createProjectAsset] = useCreateProjectAssetMutation();
  const [commitProjectAsset] = useCommitProjectAssetMutation();
  const [createActivity] = useCreateActivityMutation();
  const { data: uploadedFiles = [] } = useGetUploadedFilesQuery(
    {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      projectId: projectId!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      jobId: jobId!,
      uploadOrigin: assetUploadOriginEnum.enum.payment_flow,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !projectId || !jobId,
    }
  );

  // Keep a live reference of projectId for async handlers initialized once
  const projectIdRef = React.useRef<number | null>(projectId);
  React.useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  const invalidateAssets = React.useCallback(
    (uploadOrigin: AssetUploadOrigin) => {
      reduxDispatch(
        rtkApi.util.invalidateTags([{ type: 'Assets', id: uploadOrigin }])
      );
    },
    [reduxDispatch]
  );

  const uppy = useUppy(() => {
    const instance = new Uppy({
      debug: process.env.NODE_ENV === 'development',
      autoProceed: false,
    });

    const awsS3Options: AwsS3MultipartOptions = {
      companionUrl: `${process.env.UPPY_COMPANION_URL}`,
      companionHeaders: (() => {
        const token = getUser()?.token;
        if (!token) {
          throw new Error('Not authenticated: missing user token for uploads');
        }
        return { Authorization: `Bearer ${token}` };
      })(),
      createMultipartUpload: async function (this: any, file) {
        const currentProjectId = projectIdRef.current;
        if (currentProjectId) {
          const asset = await createProjectAsset({
            projectId: currentProjectId,
            filename: file.name,
            contentType: file.type ?? '',
            jobId: jobId,
            metadata: {
              uploadOrigin: assetUploadOriginEnum.enum.payment_flow,
            },
          }).unwrap();
          file.meta['assetId'] = asset.asset_id ?? '';
          return this.createMultipartUpload(file);
        }

        throw new Error('Project is not ready for uploads.');
      },
    };

    instance.use(AwsS3Multipart, awsS3Options);

    if (autoUpload) {
      instance.on('files-added', async () => {
        const result = await instance.upload();
        const currentProjectId = projectIdRef.current;

        if (result?.successful.length > 0) {
          if (currentProjectId) {
            const promises = result.successful.flatMap((file) => {
              const assetId =
                typeof file.meta.assetId === 'string' && file.meta.assetId
                  ? file.meta.assetId
                  : null;
              return assetId
                ? [
                    commitProjectAsset({
                      projectId: currentProjectId,
                      assetId,
                    }),
                  ]
                : [];
            });

            await Promise.all(promises);

            // Trigger re-fetch to show uploaded files after all commits are completed
            invalidateAssets(assetUploadOriginEnum.enum.payment_flow);

            const createdAssetIds = result.successful
              .map((file) => file.meta.assetId)
              .filter(
                (id): id is string => typeof id === 'string' && id.length > 0
              );

            if (createdAssetIds.length > 0) {
              createActivity({
                projectId: currentProjectId,
                activity: {
                  type: ACTIVITY_TYPES.file,
                  title: 'UPLOADED FILES',
                  createdAssetIds,
                  jobId: jobId,
                  projectId: currentProjectId,
                  description: 'UPLOADED FILES',
                  metadata: {
                    uploadOrigin: assetUploadOriginEnum.enum.payment_flow,
                  },
                },
              });
            }
          }

          instance.reset();
          dispatch(uploadActions.reset());
        }
      });
    }

    instance
      .on('file-added', (file) => {
        dispatch(
          uploadActions.addFile({
            id: file.id,
            name: file.name,
            type: file.type,
          })
        );
      })
      .on('complete', (result: UploadResult) => {
        dispatch(uploadActions.setUploading(false));
        if (onUploadComplete) onUploadComplete(result);
      })
      .on('upload-success', (file) => {
        dispatch(
          uploadActions.setFileCompleted({ id: file.id, completed: true })
        );
      })
      .on('file-removed', (file) => {
        dispatch(uploadActions.removeFile(file.id));
      })
      .on('upload-progress', (file, progress) => {
        dispatch(
          uploadActions.updateProgress({
            id: file.id,
            progress: (progress.bytesUploaded / progress.bytesTotal) * 100,
          })
        );
      })
      .on('upload', () => {
        dispatch(uploadActions.setUploading(true));
        dispatch(uploadActions.setError(null));
        dispatch(uploadActions.clearAllFileErrors());
      })
      .on('error', (error) => {
        dispatch(uploadActions.setError(error?.message ?? 'Upload failed'));
        dispatch(uploadActions.setUploading(false));
      })
      .on('upload-error', (file, err) => {
        dispatch(
          uploadActions.setFileError({
            id: file.id,
            error: err?.message ?? 'Upload failed',
          })
        );
        dispatch(uploadActions.setError(err?.message ?? 'Upload failed'));
      });

    return instance;
  });

  useMount(() => () => {
    uppy.cancelAll();
  });

  const value = useMemo<UploadApiContext>(
    () => ({ state, dispatch, uppy, uploadedFiles }),
    [state, uppy, uploadedFiles]
  );

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};
